// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import './i18n';

import NoPage from './pages/NoPage';
import Chat from './pages/Chat';
import { Providers } from './components/provider';
import AuthCheck from './components/authCheck';
import { msalConfig } from './lib/authConfig'
import { PublicClientApplication } from '@azure/msal-browser';
import Initializer from './components/initializer';
import { ProfileProvider } from './lib/profileContext';
import UserInteractionsTable from './components/messagesData';
import UserRolesManager from './components/userRolesManager';
import AppManager from './components/appManager';
import FilesPage from './pages/FilesPage';
import FAQPage from './pages/FAQPage';
import ContactsPage from './pages/ContactsPage';
import DistroPage from './pages/DistroPage';
import AdminDashboard from './components/adminDashboard';
import DevUserSwitcher from './components/devUserSwitcher';
import ErrorBoundary from './lib/ErrorBoundary';
import ConfigPage from './pages/Config';
import { LoginCallback, Security } from '@okta/okta-react';
import { oktaConfig } from './lib/oktaConfig';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js'

const oktaAuth = new OktaAuth(oktaConfig)

const msalInstance = new PublicClientApplication(msalConfig);
const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri: string) => {
    window.location.href = toRelativeUrl(originalUri || '/', window.location.origin)
  }

export default function App() {
    return (
        <Providers>
            <BrowserRouter>
                <Routes>

                    <Route path="admin" element={<AuthCheck><AdminDashboard /></AuthCheck>}>
                        <Route index element={<Navigate to="faq" replace />} />
                        <Route path="apps-manager" element={<AppManager />} />
                        <Route path="roles" element={<UserRolesManager />} />
                        <Route path="messages" element={<UserInteractionsTable />} />
                        <Route path="files" element={<FilesPage />} />
                        <Route path="faq" element={<FAQPage />} />
                        <Route path="contacts" element={<ContactsPage />} />
                        <Route path="distro" element={<DistroPage />} />
                        <Route path="config" element={<ConfigPage />} />
                    </Route>

                    <Route path="/" element={<AuthCheck><Chat /></AuthCheck>} />

                    <Route path="/login/callback" element={<LoginCallback />} />

                    <Route path="*" element={<AuthCheck><NoPage /></AuthCheck>} />
                </Routes>
            </BrowserRouter>
            <DevUserSwitcher />
        </Providers>
    );
}

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
    <>
        <ErrorBoundary>
            <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
                <MsalProvider instance={msalInstance}>
                    <Initializer msalInstance={msalInstance}>
                        <ProfileProvider>
                            <App />
                        </ProfileProvider>
                    </Initializer>
                </MsalProvider>
            </Security>
        </ErrorBoundary>
    </>
);
